import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import {
  AddressInput,
  enumAddressType,
  enumFreightTerm,
  enumPaymentTerm,
  enumSalesOrderStatus,
  enumSalesOrderType,
  enumUnit,
  enumWeightDisplayUnit,
  FreightTerm,
  MoneyInput,
  PaymentTerm,
  SalesOrderStatus,
  SalesOrderType,
  Unit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleDialog, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { SalesOrderWizardStepContractInfo } from './SalesOrderWizardStepContractInfo';
import { SalesOrderWizardStepMaterials } from './SalesOrderWizardStepMaterials';

import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';
import * as state from '~/modules/trade/_state/salesOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { toMoney, toMoneyInput, zeroMoney } from '~/modules/trade/utils/money';

type SalesOrderWizardProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (createdOrderId?: string) => void;
  draftSalesOrderId?: string; // Optional prop for editing existing draft
};

interface Step {
  number: number;
  title: string;
}

interface SalesOrderData {
  documentNumber: string;
  type: SalesOrderType;
  status: SalesOrderStatus;
  startDate?: CalendarDate; // CalendarDate type from the form
  endDate?: CalendarDate; // CalendarDate type from the form
  accountId: string;
  counterpartyReferenceNumber?: string | null;
  contactId?: string | null;
  facilityId: string;
  accountRepresentativeId?: string | null;
  shippingAddress?: AddressInput;
  billingAddress?: AddressInput;
  paymentCurrency: string;
  freightTerm: FreightTerm;
  paymentTerm: PaymentTerm;
  doNotShip?: boolean;
  externalNotes?: string | null;
  internalNotes?: string | null;
  completionNotes?: string | null;
  customAttributes?: Record<string, string> | null;
  lineItems?: Array<{
    id?: string;
    itemId: string;
    unit: Unit;
    quantity: number;
    unitPrice: MoneyInput;
    totalPrice: MoneyInput;
  }>;
  weightDisplayUnit?: WeightDisplayUnit;
  bookingIds?: string[];
}

export const SalesOrderWizard = ({
  isOpen,
  onOpenChange,
  onSuccess,
  draftSalesOrderId: draftSalesOrderIdProp,
}: SalesOrderWizardProps) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const toast = useToast();
  const t = useTranslations();
  const [currentStep, setCurrentStep] = useAtom(state.$currentStep);
  const form = useAtomValue(state.$salesOrderForm);
  const resetForm = useSetAtom(state.$resetSalesOrderForm);
  const validateForm = useSetAtom(state.$validateSalesOrderForm);
  const validateFormDraft = useSetAtom(state.$validateSalesOrderFormDraft);
  const setSalesOrderForm = useSetAtom(state.$setSalesOrderForm);
  const setSalesOrderId = useSetAtom(state.$setSalesOrderId);
  const createSalesOrderMutation = useSetAtom(state.$createSalesOrder);
  const updateSalesOrderMutation = useSetAtom(state.$updateSalesOrder);
  const nextDocumentNumberMutation = useSetAtom(state.$nextDocumentNumber);
  const [currentDraftId, setCurrentDraftId] = useAtom(state.$currentDraftId);
  const { data: existingSalesOrder, isLoading: _isLoadingExisting } =
    useAtomValue(state.$existingSalesOrder);

  const draftSalesOrderId =
    draftSalesOrderIdProp ??
    (form.id && !form.id.startsWith('temp-') ? form.id : null);

  // Set current draft ID and initialize form when wizard opens
  React.useEffect(() => {
    if (isOpen && !draftSalesOrderIdProp) {
      // Generate temporary ID first so form updates persist
      const tempId = `temp-${Date.now()}`;
      setCurrentDraftId(tempId);
      setSalesOrderId(tempId);

      // Now set the document number - it will persist because form has an ID
      nextDocumentNumberMutation({}).then((data) => {
        setSalesOrderForm({
          salesOrderNumber: data.tradeSalesOrderNextDocumentNumber,
        });
      });
    } else if (isOpen && draftSalesOrderIdProp) {
      // Set current draft ID - this will trigger persistent storage load
      setCurrentDraftId(draftSalesOrderIdProp);
      // CRITICAL: Set form ID so that changes can be saved to localStorage
      setSalesOrderId(draftSalesOrderIdProp);
    }
  }, [
    isOpen,
    draftSalesOrderIdProp,
    setCurrentDraftId,
    setSalesOrderForm,
    nextDocumentNumberMutation,
    setSalesOrderId,
  ]);

  // Load from server when opening an existing draft
  React.useEffect(() => {
    // ALWAYS load from server when opening existing draft, ignore localStorage
    // This prevents loading stale data when the same ID was used for different drafts
    if (
      existingSalesOrder &&
      isOpen &&
      draftSalesOrderId &&
      currentDraftId === draftSalesOrderId &&
      form.id === draftSalesOrderId && // Form has been populated with the correct ID
      form.salesOrderNumber !== existingSalesOrder.documentNumber // Only load if not already loaded
    ) {
      const salesOrder = existingSalesOrder;

      // Map the sales order data to the form state
      setSalesOrderForm({
        id: draftSalesOrderId, // Set the form ID to match the draft
        salesOrderNumber: salesOrder.documentNumber ?? '',
        facilityId: salesOrder.facility?.id ?? '',
        customerId: salesOrder.account?.id ?? '',
        contactId: salesOrder.contact?.id ?? '',
        accountRepresentativeId: salesOrder.accountRepresentative?.id ?? '',
        salesOrderStartDate: salesOrder.startDate
          ? maybeParseCalendarDate(salesOrder.startDate.toString())
          : undefined,
        salesOrderEndDate: salesOrder.endDate
          ? maybeParseCalendarDate(salesOrder.endDate.toString())
          : undefined,
        counterpartyReferenceNumber:
          salesOrder.counterpartyReferenceNumber ?? '',
        shippingAddress:
          (salesOrder.shippingAddress?.city?.length ?? 0) > 0
            ? {
                addressType:
                  salesOrder.shippingAddress?.addressType ??
                  enumAddressType.Standard,
                isMailingAddress: true,
                streetAddress:
                  (salesOrder.shippingAddress?.streetAddress as string) ?? '',
                city: salesOrder.shippingAddress?.city ?? '',
                state: salesOrder.shippingAddress?.state ?? '',
                postalCode: salesOrder.shippingAddress?.postalCode ?? '',
                country: salesOrder.shippingAddress?.country ?? '',
              }
            : undefined,
        billingAddress:
          (salesOrder.billingAddress?.city?.length ?? 0) > 0
            ? {
                addressType:
                  salesOrder.billingAddress?.addressType ??
                  enumAddressType.Standard,
                isMailingAddress: true,
                streetAddress:
                  (salesOrder.billingAddress?.streetAddress as string) ?? '',
                city: salesOrder.billingAddress?.city ?? '',
                state: salesOrder.billingAddress?.state ?? '',
                postalCode: salesOrder.billingAddress?.postalCode ?? '',
                country: salesOrder.billingAddress?.country ?? '',
              }
            : undefined,
        paymentCurrency: salesOrder.paymentCurrency ?? '',
        freightTerm: salesOrder.freightTerm || enumFreightTerm.FOB,
        paymentTerm: salesOrder.paymentTerm || enumPaymentTerm.Net30,
        externalNotes: salesOrder.recyclerExternalNotes ?? '',
        internalNotes: salesOrder.recyclerInternalNotes ?? '',
        completionNotes: salesOrder.completionNotes ?? '',
        customAttributes: salesOrder.customAttributes || {},
        lineItems:
          salesOrder.lineItems?.map((item) => ({
            id: item.id,
            itemId: item.itemId ?? '',
            unit: item.unit ?? enumUnit.Each,
            quantity: Number(item.quantity),
            unitPrice: item.unitPrice ? toMoney(item.unitPrice) : zeroMoney(),
            totalPrice: item.totalPrice
              ? toMoney(item.totalPrice)
              : zeroMoney(),
          })) ?? [],
        isSubmitting: false,
        bookings:
          salesOrder.bookings?.map((booking) => ({
            id: booking.id,
            bookingNumber: booking.bookingNumber,
          })) ?? [],
      });
    }
  }, [
    existingSalesOrder,
    isOpen,
    setSalesOrderForm,
    form.id,
    form.salesOrderNumber,
    draftSalesOrderId,
    currentDraftId,
    form.lineItems.length,
  ]);

  const wizardSteps: Step[] = React.useMemo(
    () => [
      {
        number: 1,
        title: t.salesOrdersView.wizard.steps.contractInfo,
      },
      {
        number: 2,
        title: t.salesOrdersView.wizard.steps.materials,
      },
    ],
    [t],
  );

  const isFirstStep = currentStep === 1;
  const isEditingDraft = !!draftSalesOrderId;
  const isMutationPending = form.isSubmitting || isSubmitting;

  // Helper function to get field name translations
  const getFieldName = React.useCallback(
    (key: string) => {
      switch (key) {
        case 'salesOrderNumber':
          return t.salesOrdersView.wizard.validation.fieldNames
            .salesOrderNumber;
        case 'facilityId':
          return t.salesOrdersView.wizard.validation.fieldNames.facilityId;
        case 'customerId':
          return t.salesOrdersView.wizard.validation.fieldNames.customerId;
        case 'salesOrderStartDate':
          return t.salesOrdersView.wizard.validation.fieldNames
            .salesOrderStartDate;
        case 'paymentCurrency':
          return t.salesOrdersView.wizard.validation.fieldNames.paymentCurrency;
        case 'freightTerm':
          return t.salesOrdersView.wizard.validation.fieldNames.freightTerm;
        case 'paymentTerm':
          return t.salesOrdersView.wizard.validation.fieldNames.paymentTerm;
        default:
          return key;
      }
    },
    [t],
  );

  // Helper function to show validation error messages
  const showValidationError = React.useCallback(
    (errors: Record<string, unknown>) => {
      const fieldNames = Object.keys(errors)
        .filter((key) => !key.startsWith('lineItem|'))
        .map(getFieldName);

      if (
        fieldNames.length === 0 &&
        Object.keys(errors).some((key) => key.startsWith('lineItem|'))
      ) {
        toast.error(t.salesOrdersView.wizard.validation.lineItemsRequired);
        return;
      }

      const errorMessage =
        fieldNames.length === 1
          ? t.salesOrdersView.wizard.validation.requiredField(fieldNames[0])
          : t.salesOrdersView.wizard.validation.requiredFields(
              fieldNames.join(', '),
            );

      toast.error(errorMessage);
    },
    [getFieldName, t, toast],
  );

  // Helper function to validate and format addresses
  const getValidAddress = React.useCallback(
    (address: AddressInput) =>
      address?.city?.length > 0 ? address : undefined,
    [],
  );

  // Helper function to map line items
  const mapLineItems = React.useCallback(
    () =>
      form.lineItems.map((item) => ({
        ...(item.id.startsWith('temp-') ? {} : { id: item.id }),
        itemId: item.itemId ?? '',
        unit: item.unit as Unit,
        quantity: item.quantity ?? 0,
        unitPrice: toMoneyInput(item.unitPrice),
        totalPrice: toMoneyInput(item.totalPrice),
      })),
    [form.lineItems],
  );

  // Helper function to build sales order data
  const buildSalesOrderData = React.useCallback(
    (status: SalesOrderStatus, type: SalesOrderType): SalesOrderData => ({
      documentNumber: form.salesOrderNumber ?? '',
      type,
      status,
      startDate: form.salesOrderStartDate ?? undefined,
      endDate: form.salesOrderEndDate ?? undefined,
      accountId: form.customerId ?? '',
      counterpartyReferenceNumber: form.counterpartyReferenceNumber,
      contactId: form.contactId,
      facilityId: form.facilityId ?? '',
      accountRepresentativeId: form.accountRepresentativeId,
      shippingAddress: form.shippingAddress
        ? getValidAddress(form.shippingAddress)
        : undefined,
      billingAddress: form.billingAddress
        ? getValidAddress(form.billingAddress)
        : undefined,
      paymentCurrency: form.paymentCurrency ?? '',
      freightTerm: form.freightTerm ?? enumFreightTerm.FOB,
      paymentTerm: form.paymentTerm ?? enumPaymentTerm.Net30,
      doNotShip: form.doNotShip,
      externalNotes: form.externalNotes,
      internalNotes: form.internalNotes,
      completionNotes: form.completionNotes,
      customAttributes: form.customAttributes as Record<string, string>,
      lineItems: currentStep === 2 ? mapLineItems() : undefined,
      weightDisplayUnit:
        form.weightDisplayUnit ?? enumWeightDisplayUnit.PriceUnit,
      bookingIds: form.bookings?.map((booking) => booking.id) ?? [],
    }),
    [form, getValidAddress, mapLineItems],
  );

  // Helper function to execute sales order mutation
  const executeSalesOrderMutation = React.useCallback(
    async (
      salesOrderData: SalesOrderData,
      isDraft: boolean,
    ): Promise<string | undefined> => {
      try {
        setIsSubmitting(true);

        if (isEditingDraft) {
          await updateSalesOrderMutation({
            id: draftSalesOrderId,
            ...salesOrderData,
            status: isDraft
              ? undefined
              : (salesOrderData.status as SalesOrderStatus),
            shippingAddress:
              salesOrderData.shippingAddress as unknown as AddressInput,
            billingAddress:
              salesOrderData.billingAddress as unknown as AddressInput,
          });
          return draftSalesOrderId;
        } else {
          const results = await createSalesOrderMutation({
            ...salesOrderData,
            status: salesOrderData.status as SalesOrderStatus,
            shippingAddress:
              salesOrderData.shippingAddress as unknown as AddressInput,
            billingAddress:
              salesOrderData.billingAddress as unknown as AddressInput,
            accountId: form.customerId as string,
            facilityId: form.facilityId as string,
            paymentCurrency: form.paymentCurrency as string,
            freightTerm: form.freightTerm as FreightTerm,
            paymentTerm: form.paymentTerm as PaymentTerm,
            // This is called on step 1, before setting materials, so let's not pass any line items
            lineItems: isDraft ? [] : salesOrderData.lineItems,
            weightDisplayUnit: isDraft
              ? undefined
              : salesOrderData.weightDisplayUnit,
          });

          const createdId = results.tradeSalesOrderCreate.id;

          // Update the form with the new sales order id
          setSalesOrderForm({ id: createdId });

          if (!isDraft) {
            setSalesOrderId(createdId);
          }

          return createdId;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isEditingDraft,
      draftSalesOrderId,
      updateSalesOrderMutation,
      createSalesOrderMutation,
      form,
      setSalesOrderId,
      setSalesOrderForm,
    ],
  );

  const handleClose = React.useCallback(() => {
    resetForm();
    setCurrentStep(1);
    onOpenChange(false);
  }, [resetForm, setCurrentStep, onOpenChange]);

  const handleNext = React.useCallback(() => {
    const validation = validateForm();

    if (validation.isValid && currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    } else if (!validation.isValid) {
      showValidationError(validation.errors);
    }
  }, [
    validateForm,
    currentStep,
    wizardSteps.length,
    showValidationError,
    setCurrentStep,
  ]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  const handleContinueAsDraft = React.useCallback(async () => {
    const validation = validateFormDraft();
    if (!validation.isValid) {
      showValidationError(validation.errors);
      return;
    }

    try {
      const salesOrderData = buildSalesOrderData(
        enumSalesOrderStatus.Draft,
        enumSalesOrderType.International,
      );
      const createdId = await executeSalesOrderMutation(salesOrderData, true);

      const isLastStep = currentStep === wizardSteps.length;

      if (isLastStep) {
        // On final step, "Save as draft" should close the wizard and refresh list
        onSuccess?.(createdId);
        handleClose();
      } else {
        // On other steps, "Continue as draft" should move to next step
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(t.salesOrdersView.wizard.errors.saveFailed);
    }
  }, [
    validateFormDraft,
    showValidationError,
    buildSalesOrderData,
    executeSalesOrderMutation,
    currentStep,
    wizardSteps.length,
    onSuccess,
    handleClose,
    toast,
    t,
    setCurrentStep,
  ]);

  const handleSubmit = React.useCallback(async () => {
    if (form.lineItems.length === 0) {
      toast.error(t.salesOrdersView.wizard.validation.noMaterials);
      return;
    }

    const validation = validateForm();

    if (!validation.isValid) {
      showValidationError(validation.errors);
      return;
    }

    try {
      const salesOrderData = buildSalesOrderData(
        enumSalesOrderStatus.Open,
        enumSalesOrderType.International,
      );

      const createdId = await executeSalesOrderMutation(salesOrderData, false);

      onSuccess?.(createdId);
      handleClose();
    } catch (error) {
      console.error('Error creating/updating sales order:', error);
      toast.error(t.salesOrdersView.wizard.errors.saveFailed);
    }
  }, [
    form.lineItems,
    toast,
    t,
    validateForm,
    showValidationError,
    buildSalesOrderData,
    executeSalesOrderMutation,
    onSuccess,
    handleClose,
  ]);

  return (
    <ConsoleDialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      askBeforeClosing={isEditingDraft}
      className="@SalesOrderWizard"
      size="fill"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>
              {isEditingDraft
                ? t.salesOrdersView.wizard.editTitle
                : t.salesOrdersView.wizard.title}
            </span>
            <span className="font-light">
              {form.salesOrderNumber ?? 'Draft'}
            </span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={(() => {
        switch (currentStep) {
          case 1:
            return <SalesOrderWizardStepContractInfo />;
          case 2:
            return <SalesOrderWizardStepMaterials />;
          default:
            return null;
        }
      })()}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isPending={isMutationPending}
              >
                {t.salesOrdersView.wizard.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            {/* CONTINUE AS DRAFT | SAVE AS DRAFT */}
            <Button
              color="neutral"
              variant="outline"
              onPress={handleContinueAsDraft}
              isPending={isMutationPending}
              isLoading={isMutationPending}
            >
              {isFirstStep
                ? t.salesOrdersView.wizard.buttons.continueAsDraft
                : t.salesOrdersView.wizard.buttons.saveAsDraft}
            </Button>

            {/* SAVE | NEXT */}
            <Button
              color="primary"
              variant="solid"
              onPress={isFirstStep ? handleNext : handleSubmit}
              isPending={isMutationPending}
            >
              {isFirstStep
                ? t.salesOrdersView.wizard.buttons.next
                : t.salesOrdersView.wizard.buttons.saveSalesOrder}
            </Button>
          </div>
        </div>
      }
    />
  );
};
